/**
 * Price Estimator — builds a stable baseline pricing catalog from the rule
 * registry and can optionally evaluate admin sandbox overrides.
 */
import {
	getRuleValue,
	RULES_BY_ID,
	type RuleOverrides,
} from "../../data/ruleRegistry";
import type { MaterialRow } from "../../engines/types";

export type PriceSourceMode = "baseline" | "sandbox";
export type PriceMatchType = "rule-id" | "keyword" | "alias" | "unpriced";

export interface PriceEntry {
	ruleId: string;
	keyword: string;
	pricePerUnit: number;
	unit: string;
	comment?: string;
}

export interface PriceEstimateResult {
	price: number;
	priceEntry: PriceEntry | null;
	parsedQty: ReturnType<typeof parseQuantity>;
	matchType: PriceMatchType;
}

interface EstimateOptions {
	mode?: PriceSourceMode;
	overrides?: RuleOverrides;
	ruleId?: string;
}

const PRICE_RULE_META: Record<string, { keyword: string; unitShort: string }> =
	{
		"prices.golvTakBjalkar": {
			keyword: "Golv- & Takbjälkar",
			unitShort: "lpm",
		},
		"prices.golvBjalkar": { keyword: "Golvbjälkar", unitShort: "lpm" },
		"prices.takbjalkar45x145": {
			keyword: "Takbjälkar 45×145",
			unitShort: "lpm",
		},
		"prices.takbjalkar45x170": {
			keyword: "Takbjälkar 45×170",
			unitShort: "lpm",
		},
		"prices.takbjalkar45x195": {
			keyword: "Takbjälkar 45×195",
			unitShort: "lpm",
		},
		"prices.takbjalkar45x220": {
			keyword: "Takbjälkar 45×220",
			unitShort: "lpm",
		},
		"prices.limtraTakbalk": {
			keyword: "Takbalk limträ",
			unitShort: "lpm",
		},
		"prices.vaggreglar": { keyword: "Väggreglar", unitShort: "lpm" },
		"prices.syll": { keyword: "Syll", unitShort: "lpm" },
		"prices.hammarband": { keyword: "Hammarband", unitShort: "lpm" },
		"prices.stenullsisolering": {
			keyword: "Stenullsisolering",
			unitShort: "m²",
		},
		"prices.bastufolie": { keyword: "Bastufolie", unitShort: "m²" },
		"prices.alutejp": { keyword: "Alutejp / fogtejp", unitShort: "paket" },
		"prices.spiklakt": { keyword: "Spikläkt", unitShort: "lpm" },
		"prices.vindskydd": { keyword: "Vindskydd", unitShort: "m²" },
		"prices.ytterpanel": { keyword: "Ytterpanel", unitShort: "lpm" },
		"prices.bastupanel": { keyword: "Bastupanel", unitShort: "lpm" },
		"prices.bastupanelAsp": { keyword: "Bastupanel (asp)", unitShort: "lpm" },
		"prices.bastupanelAl": { keyword: "Bastupanel (al)", unitShort: "lpm" },
		"prices.lavvirke": { keyword: "Lav-virke", unitShort: "lpm" },
		"prices.lavvirkeAl": { keyword: "Lav-virke (al)", unitShort: "lpm" },
		"prices.lavvirkeGran": { keyword: "Lav-virke (gran)", unitShort: "lpm" },
		"prices.tjarvitriol": { keyword: "Tjärvitriol", unitShort: "liter" },
		"prices.jarnvitriol": { keyword: "Järnvitriol", unitShort: "liter" },
		"prices.slamfarg": { keyword: "Slamfärg", unitShort: "liter" },
		"prices.fukttaligtGolv": { keyword: "Fukttåligt golv", unitShort: "m²" },
		"prices.knutbrador": { keyword: "Knutbrädor", unitShort: "lpm" },
		"prices.fonsterfoder": {
			keyword: "Fönster- / dörrfoder",
			unitShort: "lpm",
		},
		"prices.vindskivor": { keyword: "Vindskivor", unitShort: "lpm" },
		"prices.nederkantsavslut": {
			keyword: "Nederkantsavslut",
			unitShort: "lpm",
		},
		"prices.takfotsavslut": { keyword: "Takfotsavslut", unitShort: "m²" },
		"prices.undergolv": { keyword: "Undergolv / Trossbotten", unitShort: "m²" },
		"prices.trossbottenlakt": {
			keyword: "Trossbottenläkt",
			unitShort: "lpm",
		},
		"prices.kortlingRegel": {
			keyword: "Kortling / Avväxling 45×95",
			unitShort: "lpm",
		},
		"prices.bastuDorr": { keyword: "Bastudörr", unitShort: "st" },
		"prices.stavvirke": { keyword: "Stavvirke", unitShort: "lpm" },
		"prices.gavlar": { keyword: "Gavlar", unitShort: "st" },
		"prices.stalband": { keyword: "Stålband", unitShort: "st" },
		"prices.vaggor": { keyword: "Vaggor", unitShort: "st" },
		"prices.golvbrädor": { keyword: "Golvbrädor", unitShort: "m²" },
		"prices.bastuaggregat": { keyword: "Bastuaggregat", unitShort: "st" },
		"prices.elaggregat": { keyword: "Elaggregat", unitShort: "st" },
		"prices.aggregatsten": { keyword: "Aggregatsten", unitShort: "st" },
		"prices.styrpanel": { keyword: "Styrpanel", unitShort: "st" },
		"prices.ventiler": { keyword: "Ventiler", unitShort: "st" },
		"prices.fonster": { keyword: "Fönster", unitShort: "st" },
		"prices.mellendorr": { keyword: "Mellendörr", unitShort: "st" },
		"prices.panoramaglas": { keyword: "Panoramaglas", unitShort: "m²" },
		"prices.stralskydd": { keyword: "Strålskydd", unitShort: "st" },
		"prices.ytterdorr": { keyword: "Ytterdörr", unitShort: "st" },
		"prices.betongplint": { keyword: "Betongplint", unitShort: "st" },
		"prices.stolpsko": { keyword: "Stolpsko / infästning", unitShort: "st" },
		"prices.barlina": { keyword: "Bärlina", unitShort: "lpm" },
		"prices.kantbrada": { keyword: "Kantbräda", unitShort: "lpm" },
		"prices.markduk": { keyword: "Markduk", unitShort: "m²" },
		"prices.draneringsgrus": {
			keyword: "Dräneringsgrus / makadam",
			unitShort: "m²",
		},
		"prices.raspont": { keyword: "Råspont", unitShort: "m²" },
		"prices.underlagspapp": { keyword: "Underlagspapp", unitShort: "m²" },
		"prices.ytpapp": { keyword: "Ytpapp", unitShort: "m²" },
		"prices.takplat": { keyword: "Takplåt", unitShort: "m²" },
		"prices.takfotsplat": { keyword: "Takfotsplåt", unitShort: "lpm" },
		"prices.vindskiveplat": { keyword: "Vindskiveplåt", unitShort: "lpm" },
		"prices.nockbeslag": { keyword: "Nockbeslag", unitShort: "lpm" },
		"prices.silikonkabel": { keyword: "Silikonkabel", unitShort: "lpm" },
		"prices.standardKabel": { keyword: "Standard Kabel", unitShort: "lpm" },
		"prices.kopplingsmaterial": {
			keyword: "Kopplingsmaterial",
			unitShort: "paket",
		},
		"prices.bastuarmatur": { keyword: "Bastuarmatur", unitShort: "st" },
		"prices.rostfriSkruv": {
			keyword: "Rostfri skruv / beslagspaket",
			unitShort: "paket",
		},
		"prices.trallskruv": { keyword: "Trallskruv", unitShort: "paket" },
		"prices.ytbehandling": {
			keyword: "Ytbehandling / färg / olja",
			unitShort: "paket",
		},
		"prices.bastuskorsten": { keyword: "Bastuskorsten", unitShort: "st" },
		"prices.bastuskorstenForlangning": {
			keyword: "Förlängning bastuskorsten 1,0 m",
			unitShort: "st",
		},
		"prices.bastuskorstenForlangningKort": {
			keyword: "Förlängning bastuskorsten 0,5 m",
			unitShort: "st",
		},
		"prices.brandtatning": { keyword: "Brandtätning", unitShort: "st" },
		"prices.heaterModel.harvia-m3": { keyword: "Harvia M3", unitShort: "st" },
		"prices.heaterModel.harvia-m3-sl": {
			keyword: "Harvia M3 SL",
			unitShort: "st",
		},
		"prices.heaterModel.harvia-legend-150": {
			keyword: "Harvia Legend 150",
			unitShort: "st",
		},
		"prices.heaterModel.harvia-legend-150-sl": {
			keyword: "Harvia Legend 150 SL",
			unitShort: "st",
		},
		"prices.heaterModel.harvia-vega-bc60": {
			keyword: "Harvia Vega BC60",
			unitShort: "st",
		},
		"prices.heaterModel.harvia-vega-bc90": {
			keyword: "Harvia Vega BC90",
			unitShort: "st",
		},
		"prices.brandskivaKalciumsilikat": {
			keyword: "Brandskiva kalciumsilikat",
			unitShort: "st",
		},
	};

function getCatalogPrice(
	ruleId: string,
	mode: PriceSourceMode,
	overrides: RuleOverrides,
): number {
	if (mode === "sandbox") {
		return getRuleValue<number>(ruleId, overrides);
	}

	const rule = RULES_BY_ID.get(ruleId);
	return Number(rule?.defaultValue ?? 0);
}

function buildPriceEntries(
	overrides: RuleOverrides,
	mode: PriceSourceMode,
): PriceEntry[] {
	return Object.entries(PRICE_RULE_META)
		.map(([ruleId, meta]) => {
			const rule = RULES_BY_ID.get(ruleId);
			return {
				ruleId,
				keyword: meta.keyword,
				pricePerUnit: getCatalogPrice(ruleId, mode, overrides),
				unit: meta.unitShort,
				comment: rule?.description,
			};
		})
		.sort((left, right) => right.keyword.length - left.keyword.length);
}

/**
 * Baseline entries are a pure function of module constants — `getCatalogPrice`
 * ignores `overrides` entirely in that mode and reads `RULES_BY_ID` — so they
 * are built once for the process.
 *
 * Worth caching because this runs per ROW, not per calculation: a builder
 * keystroke prices ~45 BOM rows for the summary plus ~20 what-if repricings
 * for the finish swatches, and each one was rebuilding and re-sorting the
 * whole 72-entry catalog. Sandbox mode still rebuilds — its numbers depend on
 * the caller's overrides.
 *
 * Callers therefore share one array instance in baseline mode: treat the
 * result as read-only. Every consumer in this module only searches it.
 */
let baselinePriceEntries: PriceEntry[] | null = null;

export function getPriceEntries(
	overrides: RuleOverrides = {},
	mode: PriceSourceMode = "sandbox",
): PriceEntry[] {
	if (mode === "baseline") {
		baselinePriceEntries ??= buildPriceEntries({}, "baseline");
		return baselinePriceEntries;
	}
	return buildPriceEntries(overrides, mode);
}

export function parseQuantity(
	qty: string,
	preferredUnit?: string,
): { value: number; unit: string } | null {
	// `liter` joined the unit set with the exterior-treatment row (issue 134):
	// paint is the first BOM line whose quantity is a volume, and a unit the
	// parser cannot see silently prices the row at 0 rather than failing.
	const matches = Array.from(
		qty.matchAll(/([\d.,]+)\s*(lpm|m²|st|paket|liter)/gi),
	)
		.map((match) => {
			const value = parseFloat(match[1].replace(",", "."));
			const unit = match[2];
			return Number.isNaN(value) ? null : { value, unit };
		})
		.filter((match): match is { value: number; unit: string } =>
			Boolean(match),
		);

	if (matches.length === 0) {
		return null;
	}

	if (preferredUnit) {
		const preferredMatches = matches.filter(
			(match) => match.unit.toLowerCase() === preferredUnit.toLowerCase(),
		);
		if (preferredMatches.length > 0) {
			return {
				value: Number(
					preferredMatches
						.reduce((sum, match) => sum + match.value, 0)
						.toFixed(2),
				),
				unit: preferredMatches[0].unit,
			};
		}
	}

	return matches[0];
}

function findEntryByRuleId(
	ruleId: string | undefined,
	entries: PriceEntry[],
): PriceEntry | null {
	if (!ruleId) {
		return null;
	}

	return entries.find((entry) => entry.ruleId === ruleId) ?? null;
}

function findEntryByKeyword(
	materialName: string,
	entries: PriceEntry[],
): PriceEntry | null {
	const lowerName = materialName.toLowerCase();
	return (
		entries.find((entry) => lowerName.includes(entry.keyword.toLowerCase())) ??
		null
	);
}

function matchAlias(
	lowerName: string,
	entries: PriceEntry[],
): PriceEntry | null {
	const aliases: Record<string, string> = {
		åldersbeständig: "prices.bastufolie",
		"alu-tejp": "prices.alutejp",
		glespanel: "prices.spiklakt",
		"inv. spikläkt": "prices.spiklakt",
		"utv. spikläkt": "prices.spiklakt",
		"invändig panel": "prices.bastupanel",
		bastulav: "prices.lavvirke",
		"fukttåligt golv": "prices.fukttaligtGolv",
		bandtäckt: "prices.takplat",
		regnkrage: "prices.regnkrage",
		regnskydd: "prices.takhuv",
	};

	for (const [alias, ruleId] of Object.entries(aliases)) {
		if (lowerName.includes(alias)) {
			return entries.find((entry) => entry.ruleId === ruleId) ?? null;
		}
	}

	return null;
}

export function estimateRowPriceDetailed(
	materialName: string,
	quantity: string,
	options: EstimateOptions = {},
): PriceEstimateResult {
	const { mode = "sandbox", overrides = {}, ruleId } = options;
	const entries = getPriceEntries(overrides, mode);
	const lowerName = materialName.toLowerCase();

	const byRuleId = findEntryByRuleId(ruleId, entries);
	if (byRuleId) {
		const parsedQty = parseQuantity(quantity, byRuleId.unit);
		return {
			price: parsedQty
				? Math.round(byRuleId.pricePerUnit * parsedQty.value)
				: 0,
			priceEntry: byRuleId,
			parsedQty,
			matchType: parsedQty ? "rule-id" : "unpriced",
		};
	}

	const byKeyword = findEntryByKeyword(materialName, entries);
	if (byKeyword) {
		if (
			typeof globalThis !== "undefined" &&
			process?.env?.NODE_ENV !== "test"
		) {
			console.warn(
				`[DEPRECATED] priceEstimator keyword fallback hit for "${materialName}" → ${byKeyword.ruleId}. ` +
					"Add priceRuleId to the MaterialRow to use the stable rule-id path.",
			);
		}
		const parsedQty = parseQuantity(quantity, byKeyword.unit);
		return {
			price: parsedQty
				? Math.round(byKeyword.pricePerUnit * parsedQty.value)
				: 0,
			priceEntry: byKeyword,
			parsedQty,
			matchType: parsedQty ? "keyword" : "unpriced",
		};
	}

	const byAlias = matchAlias(lowerName, entries);
	if (byAlias) {
		if (
			typeof globalThis !== "undefined" &&
			process?.env?.NODE_ENV !== "test"
		) {
			console.warn(
				`[DEPRECATED] priceEstimator alias fallback hit for "${materialName}" → ${byAlias.ruleId}. ` +
					"Add priceRuleId to the MaterialRow to use the stable rule-id path.",
			);
		}
		const parsedQty = parseQuantity(quantity, byAlias.unit);
		return {
			price: parsedQty ? Math.round(byAlias.pricePerUnit * parsedQty.value) : 0,
			priceEntry: byAlias,
			parsedQty,
			matchType: parsedQty ? "alias" : "unpriced",
		};
	}

	return { price: 0, priceEntry: null, parsedQty: null, matchType: "unpriced" };
}

export function estimateMaterialRowPrice(
	row: MaterialRow,
	options: EstimateOptions = {},
): PriceEstimateResult {
	return estimateRowPriceDetailed(row.material, row.quantity, {
		...options,
		ruleId: row.priceRuleId ?? options.ruleId,
	});
}

export function estimateRowPrice(
	materialName: string,
	quantity: string,
	overrides: RuleOverrides = {},
): {
	price: number;
	priceEntry: PriceEntry | null;
	parsedQty: ReturnType<typeof parseQuantity>;
} {
	const result = estimateRowPriceDetailed(materialName, quantity, {
		overrides,
		mode: "sandbox",
	});
	return {
		price: result.price,
		priceEntry: result.priceEntry,
		parsedQty: result.parsedQty,
	};
}

export function estimateBaselineRowPrice(
	materialName: string,
	quantity: string,
	ruleId?: string,
): PriceEstimateResult {
	return estimateRowPriceDetailed(materialName, quantity, {
		mode: "baseline",
		ruleId,
	});
}

export function getBaselinePriceEntries(): PriceEntry[] {
	return getPriceEntries({}, "baseline");
}
